/**
 *  This components was build on top of the selective hydration pattern, which
 * makes possible hydrate components only when the component is to be visible
 * to the browser viewport or when main-thread was in idle status. When applied
 * to a CPU-bound or bigger component, this improves several performance
 * metrics, like TTI and TBT.
 *
 *  In recent versions of React, exists some built-in APIs that already
 * implements the selective hydration pattern, like `<Suspense />`, but we
 * encountered several difficulties to use these React APIs, like streaming,
 * moment when wrapped component is hydrated (particularly, the choose to
 * hydrate the wrapped component when is marked as resolved, can make some
 * drawbacks, increasing the time running the long processing of a page load,
 * for example) and  the upgrade  to the React v18.
 *
 *  These design decisions of React and the "issues" encountered forces we build
 * this in-house solution. This solution isn't 100% perfect, but for our
 * purpose it's good enough.
 */
import PropTypes from 'prop-types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { isBrowser, markup } from 'lib/utils';

const MILLISECONDS_TO_REQUEST_IDLE_CALLBACK = 850;

/**
 * Hydrate only once the component actually reaches the viewport. Consumers that
 * want to start earlier — to give a dynamic import time to resolve before the
 * component is on screen — opt in through the `rootMargin` prop, so the change
 * stays with them instead of moving every other consumer's hydration point.
 */
const DEFAULT_ROOT_MARGIN = '0px';

/**
 * Flips to `true` after the initial client hydration commit. React renders
 * every server-rendered instance before running any effect, so during the
 * hydration pass this stays `false` for all of them — they keep the server
 * markup untouched (no skeleton, no hydration mismatch). Instances mounted
 * afterwards (e.g. infinite-scroll items) read `true` and render the skeleton
 * fallback instead of a blank gap.
 */
let appHasHydrated = false;

// TODO: split these components into different files
// Always renders the real children on the server so SSR keeps the actual
// markup (important for SEO). The `fallback`, when provided, is a client-only
// placeholder used while the component is not yet hydrated.
const LazyHydrationOnServer = ({ children, className }) => (
  <section className={className}>{children}</section>
);

const LazyHydrationOnClient = ({
  children,
  className,
  fallback = null,
  force = false,
  placeholderSize,
  rootMargin = DEFAULT_ROOT_MARGIN,
}) => {
  const containerRef = useRef(null);
  const [isHydrated, setHydrated] = useState(force);
  const cleanups = useRef([]);

  const cleanup = () => {
    cleanups.current.map((fn) => fn());
  };

  const hydrate = () => {
    // Remove listeners first to avoid dispatch unnecessarily
    cleanup();
    setHydrated(true);
  };

  const makeIdleHydration = () => {
    const idleCallback = requestIdleCallback(
      () => {
        requestAnimationFrame(hydrate);
      },
      { timeout: MILLISECONDS_TO_REQUEST_IDLE_CALLBACK }
    );

    cleanups.current.push(() => {
      cancelIdleCallback(idleCallback);
    });
  };

  const makeVisibleHydration = () => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio <= 0) {
          return;
        }

        hydrate();
      },
      { threshold: 0, rootMargin }
    );

    observer.observe(containerRef.current);
    cleanups.current.push(() => {
      observer.disconnect();
    });
  };

  useEffect(() => {
    appHasHydrated = true;
  }, []);

  useLayoutEffect(() => {
    if (force && !isHydrated) {
      hydrate();
    }
  }, [force]);

  useEffect(() => {
    if (isHydrated) {
      return;
    }

    const initEvent = () => {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const isInViewport =
        rect.top <= viewportHeight || rect.bottom <= viewportHeight;

      /**
       * Recently Safari rolled back their implementation of
       * `requestIdleCallback` due internal inconsistencies.
       */
      if (isInViewport && 'requestIdleCallback' in window) {
        makeIdleHydration();
      } else {
        makeVisibleHydration();
      }
    };

    initEvent();

    return () => {
      cleanup();
    };
  }, []);

  if (!isHydrated) {
    /**
     * When a fallback (e.g. a skeleton) is provided, render it as real content
     * so the reserved space never appears blank while not hydrated. This is
     * what prevents the empty gaps for client-mounted items (infinite scroll)
     * that have no server markup to keep in place.
     *
     * Only do this for instances mounted after the initial hydration
     * (`appHasHydrated`); server-rendered instances keep their markup via the
     * `dangerouslySetInnerHTML` branch below to avoid a hydration mismatch.
     */
    if (fallback && appHasHydrated) {
      return (
        <section
          ref={containerRef}
          className={className}
          suppressHydrationWarning
        >
          {fallback}
        </section>
      );
    }

    return (
      <section
        className={className}
        style={
          placeholderSize
            ? {
                height: placeholderSize.height,
                width: placeholderSize.width,
              }
            : null
        }
        suppressHydrationWarning
        ref={containerRef}
        /**
         * Hacky to React don't touch in this component in client-side until
         * threshold is intersected.
         *
         * See: https://github.com/facebook/react/issues/10923#issuecomment-338715787
         */
        dangerouslySetInnerHTML={markup('')}
      />
    );
  }

  return children;
};

const LazyHydration = ({
  children,
  className,
  fallback,
  force = false,
  placeholderSize,
  rootMargin,
}) => {
  if (isBrowser()) {
    return (
      <LazyHydrationOnClient
        className={className}
        fallback={fallback}
        force={force}
        placeholderSize={placeholderSize}
        rootMargin={rootMargin}
      >
        {children}
      </LazyHydrationOnClient>
    );
  }

  // Initially mounts component in server-side for SEO purposes
  return (
    <LazyHydrationOnServer className={className}>
      {children}
    </LazyHydrationOnServer>
  );
};

LazyHydration.propTypes = {
  /**
   * The component that you want to lazy hydrate.
   */
  children: PropTypes.node.isRequired,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Client-only placeholder rendered while the component is not hydrated, for
   * instances mounted after the initial hydration — infinite-scroll items and
   * anything mounted on a client-side navigation, which have no server markup
   * to preserve. Server rendering always emits `children`, so this does not
   * affect SEO and does not spare the server from mounting the component.
   */
  fallback: PropTypes.node,
  /**
   * Forces component be hydrated as soon as possible, useful when is necessary
   * manipulates component programmatically.
   */
  force: PropTypes.bool,
  /**
   * Set the dimensions of placeholder component, reducing possible layout
   * shifts when lazy component is being hydrated.
   */
  placeholderSize: PropTypes.shape({
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  /**
   * Margin added around the viewport when deciding whether the component is
   * visible, so hydration can start before it scrolls into view. Same syntax as
   * the IntersectionObserver option of the same name.
   */
  rootMargin: PropTypes.string,
};

export default LazyHydration;
